// Генерация DOCX-документов с результатами расчёта

import {
  Document,
  Paragraph,
  TextRun,
  AlignmentType,
  Packer
} from 'docx';
import { saveAs } from 'file-saver';
import type { 
  CalculationInput, 
  VoltageLossResult, 
  HeatingResult,
  NetworkType,
  Material,
  LoadType,
  InstallationMethod,
  LineType,
  InsulationType
} from '../types';
import {
  RESISTIVITY,
  NETWORK_TYPE_LABELS,
  MATERIAL_LABELS,
  LOAD_TYPE_LABELS,
  INSTALLATION_METHOD_LABELS,
  LINE_TYPE_LABELS,
  INSULATION_TYPE_LABELS
} from '../data/constants';
import { X0_BY_SECTION } from '../data/tables';
import { 
  calculateSinPhi, 
  calculateResistance, 
  calculateReactance 
} from './calculations';
import { calculateCorrectionFactor, getTemperatureCoefficient, getBundleCoeff, getGroundCoeff } from './heating';

// ============================================================================
// ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ
// ============================================================================

/**
 * Форматирование даты для имени файла
 * Результат: "2026-04-02_14-35"
 */
function formatDateForFilename(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}_${hours}-${minutes}`;
}

/**
 * Форматирование даты для отчёта
 * Результат: "02.04.2026 14:35:22"
 */
function formatDateForReport(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
}

/**
 * Форматирование числа с заданным количеством знаков после запятой
 */
function formatNumber(value: number, decimals: number = 2): string {
  return value.toFixed(decimals);
}

/**
 * Получение метки для типа сети
 */
function getNetworkTypeLabel(type: NetworkType): string {
  return NETWORK_TYPE_LABELS[type];
}

/**
 * Получение метки для материала
 */
function getMaterialLabel(material: Material): string {
  return MATERIAL_LABELS[material];
}

/**
 * Получение метки для типа нагрузки
 */
function getLoadTypeLabel(loadType: LoadType): string {
  return LOAD_TYPE_LABELS[loadType];
}

/**
 * Получение метки для способа прокладки
 */
function getInstallationMethodLabel(method: InstallationMethod): string {
  return INSTALLATION_METHOD_LABELS[method];
}

/**
 * Получение метки для типа линии
 */
function getLineTypeLabel(lineType: LineType): string {
  return LINE_TYPE_LABELS[lineType];
}

/**
 * Получение метки для типа изоляции
 */
function getInsulationTypeLabel(insulation: InsulationType): string {
  return INSULATION_TYPE_LABELS[insulation];
}

/**
 * Получение X₀ для сечения из таблицы
 */
function getX0ForSection(crossSection: number): number {
  const sections = Object.keys(X0_BY_SECTION).map(Number).sort((a, b) => a - b);
  let nearestSection = sections[0];
  
  for (const section of sections) {
    if (section <= crossSection) {
      nearestSection = section;
    } else {
      break;
    }
  }
  
  return X0_BY_SECTION[nearestSection] ?? 0.08;
}

// ============================================================================
// ФУНКЦИИ ДЛЯ ДЕТАЛИЗАЦИИ ПОПРАВОЧНЫХ КОЭФФИЦИЕНТОВ
// ============================================================================

/**
 * Детализация температурного коэффициента
 */
function getTempCoefficientDetails(insulationType: InsulationType, ambientTemp: number): Paragraph[] {
  const kTemp = getTemperatureCoefficient(insulationType, ambientTemp);
  const insulationLabel = getInsulationTypeLabel(insulationType);
  const baseTemp = 30; // Базовая температура для воздуха по ПУЭ
  
  return [
    createTextParagraph(`     1) kтемп = ${formatNumber(kTemp, 3)} – температурный коэффициент`, { bold: false }),
    createTextParagraph(`        • Температура среды: ${ambientTemp}°C`),
    createTextParagraph(`        • Тип изоляции: ${insulationLabel}`),
    createTextParagraph(`        • Базовая температура: ${baseTemp}°C (по ПУЭ табл. 1.3.3)`),
    createTextParagraph(`        • Источник: ПУЭ-7, таблица 1.3.3 (поправка на температуру среды)`),
  ];
}

/**
 * Детализация коэффициента прокладки пучком
 */
function getBundleCoefficientDetails(cablesCount: number, installationMethod: InstallationMethod): Paragraph[] {
  const kBundle = getBundleCoeff(cablesCount);
  const methodLabel = getInstallationMethodLabel(installationMethod);
  
  const paragraphs: Paragraph[] = [
    createTextParagraph(`     2) kпучок = ${formatNumber(kBundle, 3)} – коэффициент прокладки пучком`, { bold: false }),
    createTextParagraph(`        • Количество кабелей рядом: ${cablesCount} шт.`),
    createTextParagraph(`        • Способ прокладки: ${methodLabel}`),
  ];
  
  if (cablesCount > 1 || installationMethod === 'bundle') {
    paragraphs.push(createTextParagraph(`        • Источник: ПУЭ-7, таблица 1.3.26 (коэффициент для пучков кабелей)`));
  } else {
    paragraphs.push(createTextParagraph(`        • Примечание: коэффициент = 1.0 (одиночная прокладка)`));
  }
  
  return paragraphs;
}

/**
 * Детализация коэффициента прокладки в трубе
 */
function getPipeCoefficientDetails(installationMethod: InstallationMethod): Paragraph[] {
  const kPipe = installationMethod === 'pipe' ? 0.9 : 1.0;
  
  return [
    createTextParagraph(`     3) kтруба = ${formatNumber(kPipe, 3)} – коэффициент прокладки в трубе`, { bold: false }),
    createTextParagraph(`        • Способ прокладки: ${getInstallationMethodLabel(installationMethod)}`),
    installationMethod === 'pipe'
      ? createTextParagraph(`        • Источник: ПУЭ-7, п. 1.3.10 (коэффициент 0.9 для прокладки в трубах)`)
      : createTextParagraph(`        • Примечание: коэффициент = 1.0 (прокладка не в трубе)`),
  ];
}

/**
 * Детализация коэффициента прокладки в лотке
 */
function getTrayCoefficientDetails(installationMethod: InstallationMethod): Paragraph[] {
  const kTray = installationMethod === 'tray' ? 0.95 : 1.0;
  
  return [
    createTextParagraph(`     4) kлоток = ${formatNumber(kTray, 3)} – коэффициент прокладки в лотке`, { bold: false }),
    createTextParagraph(`        • Способ прокладки: ${getInstallationMethodLabel(installationMethod)}`),
    installationMethod === 'tray'
      ? createTextParagraph(`        • Источник: ГОСТ Р 50571.5.52-2011, п. 523.7 (коэффициент 0.95 для лотков)`)
      : createTextParagraph(`        • Примечание: коэффициент = 1.0 (прокладка не в лотке)`),
  ];
}

/**
 * Детализация коэффициента прокладки в земле
 */
function getGroundCoefficientDetails(installationMethod: InstallationMethod, thermalResistance: number): Paragraph[] {
  const kGround = installationMethod === 'ground' ? getGroundCoeff(thermalResistance) : 1.0;
  
  return [
    createTextParagraph(`     5) kземля = ${formatNumber(kGround, 3)} – коэффициент прокладки в земле`, { bold: false }),
    createTextParagraph(`        • Способ прокладки: ${getInstallationMethodLabel(installationMethod)}`),
    installationMethod === 'ground'
      ? createTextParagraph(`        • Тепловое сопротивление грунта: ${thermalResistance} К·см/Вт`)
      : createTextParagraph(`        • Примечание: коэффициент = 1.0 (прокладка не в земле)`),
    installationMethod === 'ground'
      ? createTextParagraph(`        • Источник: ПУЭ-7, таблица 1.3.23 (коэффициент по тепловому сопротивлению)`)
      : createTextParagraph(''),
  ];
}

// ============================================================================
// ФУНКЦИИ СОЗДАНИЯ ЭЛЕМЕНТОВ ДОКУМЕНТА
// ============================================================================

/**
 * Создание параграфа с заголовком
 */
function createHeaderParagraph(text: string, size: number = 28): Paragraph {
  return new Paragraph({
    children: [
      new TextRun({
        text,
        bold: true,
        size,
        font: 'Times New Roman',
      }),
    ],
    alignment: AlignmentType.CENTER,
    spacing: { after: 200 },
  });
}

/**
 * Создание параграфа с текстом
 */
function createTextParagraph(
  text: string, 
  options: { 
    bold?: boolean; 
    size?: number; 
    alignment?: typeof AlignmentType[keyof typeof AlignmentType];
    spacing?: { before?: number; after?: number };
  } = {}
): Paragraph {
  return new Paragraph({
    children: [
      new TextRun({
        text,
        bold: options.bold ?? false,
        size: options.size ?? 24,
        font: 'Times New Roman',
      }),
    ],
    alignment: options.alignment ?? AlignmentType.LEFT,
    spacing: options.spacing,
  });
}

/**
 * Создание разделительной линии
 */
function createSeparatorLine(char: string = '─', count: number = 63): Paragraph {
  return new Paragraph({
    children: [
      new TextRun({
        text: char.repeat(count),
        size: 20,
        font: 'Courier New',
      }),
    ],
    spacing: { before: 100, after: 100 },
  });
}

/**
 * Создание двойной разделительной линии
 */
function createDoubleSeparatorLine(): Paragraph {
  return new Paragraph({
    children: [
      new TextRun({
        text: '═'.repeat(63),
        size: 20,
        font: 'Courier New',
      }),
    ],
    spacing: { before: 100, after: 100 },
  });
}

/**
 * Создание строки с параметром
 */
function createParamLine(label: string, value: string, unit?: string): Paragraph {
  const displayValue = unit ? `${value} ${unit}` : value;
  const padding = ' '.repeat(Math.max(1, 36 - label.length));
  return new Paragraph({
    children: [
      new TextRun({
        text: `${label}${padding}= ${displayValue}`,
        size: 24,
        font: 'Courier New',
      }),
    ],
    spacing: { after: 60 },
  });
}

/**
 * Создание пустой строки
 */
function createEmptyParagraph(): Paragraph {
  return new Paragraph({
    children: [],
  });
}

// ============================================================================
// ГЕНЕРАЦИЯ ОТЧЁТА ПОТЕРИ НАПРЯЖЕНИЯ
// ============================================================================

/**
 * Генерация отчёта потери напряжения
 */
export function generateVoltageLossReport(
  input: CalculationInput,
  result: VoltageLossResult
): Document {
  const now = new Date();
  const rho = RESISTIVITY[input.material];
  const sinPhi = calculateSinPhi(input.powerFactor);
  const resistance = calculateResistance(input.material, input.length, input.crossSection, 20);
  const reactance = calculateReactance(input.crossSection, input.length, input.lineType);
  const x0 = getX0ForSection(input.crossSection);
  const lengthKm = input.length / 1000;
  const cosPhi = input.powerFactor;
  const networkCoeff = input.networkType === 'three-phase' ? '√3' : '2';
  const networkCoeffNum = input.networkType === 'three-phase' ? Math.sqrt(3) : 2;
  
  // Используем вычисленный ток из результата расчёта
  const calculatedCurrent = result.calculatedCurrent;
  
  // Вычисление составляющих для формулы
  const rCosPhi = resistance * cosPhi;
  const xSinPhi = reactance * sinPhi;
  const sumRx = rCosPhi + xSinPhi;

  const children: Paragraph[] = [
    // Заголовок
    createDoubleSeparatorLine(),
    createHeaderParagraph('РАСЧЁТ ВЫБОРА СЕЧЕНИЯ КАБЕЛЯ'),
    createHeaderParagraph('(Потеря напряжения)', 24),
    createDoubleSeparatorLine(),
    createEmptyParagraph(),
    
    // Дата и режим
    createTextParagraph(`Дата расчёта: ${formatDateForReport(now)}`),
    createTextParagraph(`Режим расчёта: Потеря напряжения`),
    createEmptyParagraph(),
    
    // Раздел 1: Исходные данные
    createSeparatorLine(),
    createTextParagraph('1. Исходные данные', { bold: true, size: 26 }),
    createSeparatorLine(),
    createEmptyParagraph(),
    
    createParamLine('Номинальное напряжение, Uном', formatNumber(input.nominalVoltage, 0), 'В'),
    createParamLine('Ток нагрузки, I', formatNumber(calculatedCurrent, 2), 'А'),
    ...(input.power && input.power > 0
      ? [createParamLine('Мощность нагрузки, P', formatNumber(input.power, 2), 'кВт')]
      : []),
    createParamLine('Длина линии, L', formatNumber(input.length, 1), 'м'),
    createParamLine('Сечение проводника, S', formatNumber(input.crossSection, 1), 'мм²'),
    createParamLine('Коэффициент мощности, cosφ', formatNumber(input.powerFactor, 2)),
    createParamLine('Тип сети', getNetworkTypeLabel(input.networkType)),
    createParamLine('Материал проводника', getMaterialLabel(input.material)),
    createParamLine('Тип нагрузки', getLoadTypeLabel(input.loadType)),
    createParamLine('Способ прокладки', getInstallationMethodLabel(input.installationMethod)),
    createParamLine('Температура среды, T_сред', formatNumber(input.ambientTemp, 0), '°C'),
    createParamLine('Количество кабелей рядом', formatNumber(input.cablesCount, 0)),
    createParamLine('Тип линии', getLineTypeLabel(input.lineType)),
    createParamLine('Тип изоляции', getInsulationTypeLabel(input.insulationType)),
    createEmptyParagraph(),
    
    // Раздел 2: Формулы и ход расчёта
    createSeparatorLine(),
    createTextParagraph('2. Формулы и ход расчёта (с подстановкой чисел)', { bold: true, size: 26 }),
    createSeparatorLine(),
    createEmptyParagraph(),
    
    // 2.1 Активное сопротивление
    createTextParagraph('2.1. Активное сопротивление линии', { bold: true }),
    createTextParagraph('     R = ρ × L / S'),
    createTextParagraph(`     где ρ = ${formatNumber(rho, 4)} Ом·мм²/м – удельное сопротивление ${getMaterialLabel(input.material).toLowerCase()} при 20 °C`),
    createTextParagraph('         (по ГОСТ 22483-2012)'),
    createTextParagraph(`     R = ${formatNumber(rho, 4)} × ${formatNumber(input.length, 0)} / ${formatNumber(input.crossSection, 1)} = ${formatNumber(resistance, 6)} Ом`),
    createEmptyParagraph(),
    
    // 2.2 Реактивное сопротивление
    createTextParagraph('2.2. Реактивное сопротивление линии', { bold: true }),
    createTextParagraph('     X = X₀ × L_км'),
    createTextParagraph(`     Для сечения ${formatNumber(input.crossSection, 0)} мм² по таблице X₀ = ${formatNumber(x0, 2)} Ом/км`),
    createTextParagraph(`     L_км = ${formatNumber(input.length, 0)} м / 1000 = ${formatNumber(lengthKm, 3)} км`),
    createTextParagraph(`     X = ${formatNumber(x0, 2)} × ${formatNumber(lengthKm, 3)} = ${formatNumber(reactance, 6)} Ом`),
    createEmptyParagraph(),
    
    // 2.3 Синус угла
    createTextParagraph('2.3. Синус угла сдвига фаз', { bold: true }),
    createTextParagraph(`     sinφ = √(1 – cos²φ) = √(1 – ${formatNumber(cosPhi, 2)}²) = √(${formatNumber(1 - cosPhi * cosPhi, 4)}) = ${formatNumber(sinPhi, 2)}`),
    createEmptyParagraph(),
    
    // 2.4 Потеря напряжения
    createTextParagraph('2.4. Потеря напряжения', { bold: true }),
    ...(input.power && input.power > 0 ? [
      createTextParagraph('     Ток вычислен из мощности:'),
      createTextParagraph(input.networkType === 'three-phase'
        ? `     I = P / (√3 × U × cosφ) = ${formatNumber(input.power * 1000, 0)} / (√3 × ${formatNumber(input.nominalVoltage, 0)} × ${formatNumber(cosPhi, 2)}) = ${formatNumber(calculatedCurrent, 2)} А`
        : `     I = P / (U × cosφ) = ${formatNumber(input.power * 1000, 0)} / (${formatNumber(input.nominalVoltage, 0)} × ${formatNumber(cosPhi, 2)}) = ${formatNumber(calculatedCurrent, 2)} А`),
      createEmptyParagraph(),
    ] : []),
    createTextParagraph(`     ΔU = ${networkCoeff} × I × (R × cosφ + X × sinφ)`),
    createTextParagraph(`     ΔU = ${networkCoeff} × ${formatNumber(calculatedCurrent, 2)} × (${formatNumber(rCosPhi, 6)} + ${formatNumber(xSinPhi, 6)})`),
    createTextParagraph(`        = ${formatNumber(networkCoeffNum * calculatedCurrent, 2)} × ${formatNumber(sumRx, 6)} = ${formatNumber(result.voltageLoss, 2)} В`),
    createEmptyParagraph(),
    
    // 2.5 Потеря в процентах
    createTextParagraph('2.5. Потеря напряжения в процентах', { bold: true }),
    createTextParagraph(`     ΔU% = (ΔU / Uном) × 100% = (${formatNumber(result.voltageLoss, 2)} / ${formatNumber(input.nominalVoltage, 0)}) × 100% = ${formatNumber(result.voltageLossPercent, 2)}%`),
    createEmptyParagraph(),
    
    // Раздел 3: Результаты
    createSeparatorLine(),
    createTextParagraph('3. Результаты расчёта', { bold: true, size: 26 }),
    createSeparatorLine(),
    createEmptyParagraph(),
    
    createParamLine('Потеря напряжения, абсолютная', formatNumber(result.voltageLoss, 2), 'В'),
    createParamLine('Потеря напряжения, относительная', formatNumber(result.voltageLossPercent, 2), '%'),
    createParamLine('Активное сопротивление линии', formatNumber(result.resistance, 4), 'Ом'),
    createParamLine('Реактивное сопротивление линии', formatNumber(result.reactance, 4), 'Ом'),
    createParamLine('Полное сопротивление линии', formatNumber(result.impedance, 4), 'Ом'),
    createEmptyParagraph(),
    
    // Раздел 4: Заключение
    createSeparatorLine(),
    createTextParagraph('4. Заключение и соответствие нормативам', { bold: true, size: 26 }),
    createSeparatorLine(),
    createEmptyParagraph(),
    
    createTextParagraph(`Допустимая потеря напряжения для ${getLoadTypeLabel(input.loadType).toLowerCase()} сетей по ПУЭ (п. 7.23) = ${formatNumber(result.maxAllowedLoss, 1)}%`),
    createTextParagraph(`Расчётная потеря = ${formatNumber(result.voltageLossPercent, 2)}%`),
    createEmptyParagraph(),
    
    result.isWithinNorm
      ? createTextParagraph('⇒ Потери в норме. Кабель проходит проверку.', { bold: true })
      : createTextParagraph('⇒ Превышение нормы! Требуется увеличить сечение кабеля.', { bold: true }),
    createEmptyParagraph(),
    
    // Раздел 5: Комментарии
    createSeparatorLine(),
    createTextParagraph('5. Комментарии по использованным константам и нормативам', { bold: true, size: 26 }),
    createSeparatorLine(),
    createEmptyParagraph(),
    
    createTextParagraph('Использованные нормативные документы:'),
    createTextParagraph('• ГОСТ 22483-2012 «Жилы токопроводящие для кабелей, проводов и шнуров» – для удельного сопротивления материалов (табл. 1-3).'),
    createTextParagraph(`• СП256.1325800.2016 «Электроустановки низковольтные» – таблица 4.1 для реактивного сопротивления кабелей (X₀ = ${formatNumber(x0, 2)} Ом/км для сечения ${formatNumber(input.crossSection, 0)} мм²).`),
    createTextParagraph('• ПУЭ-7 (Правила устройства электроустановок), глава 7.23 – допустимые потери напряжения в сетях (5% для силовых, 2.5% для осветительных сетей).'),
    createTextParagraph('• ГОСТ Р 50571.5.52-2011 «Электроустановки низковольтные. Выбор электрооборудования и маркировка», п. 525.1 – формула расчёта падения напряжения.'),
    createEmptyParagraph(),
    createTextParagraph('Применяемость формул:'),
    createTextParagraph(`• Формула ΔU = ${networkCoeff} × I × (R × cosφ + X × sinφ) используется для расчёта потери напряжения в линиях электропередачи.`),
    createTextParagraph(`• При однофазной сети коэффициент при cosφ учитывает падение напряжения на обоих проводах (фазном и нулевом).`),
    createTextParagraph('• При трёхфазной сети коэффициент √3 учитывает линейное напряжение и симметричную нагрузку фаз.'),
    createEmptyParagraph(),
    
    // Конец отчёта
    createSeparatorLine(),
    createTextParagraph('Конец отчёта.', { alignment: AlignmentType.CENTER }),
    createSeparatorLine(),
  ];

  return new Document({
    sections: [{
      properties: {},
      children,
    }],
  });
}

// ============================================================================
// ГЕНЕРАЦИЯ ОТЧЁТА НАГРЕВА
// ============================================================================

/**
 * Генерация отчёта нагрева
 */
export function generateHeatingReport(
  input: CalculationInput,
  result: HeatingResult
): Document {
  const now = new Date();
  const thermalResistance = input.thermalResistance ?? 120;
  const correctionFactor = calculateCorrectionFactor(
    input.insulationType,
    input.ambientTemp,
    input.cablesCount,
    input.installationMethod,
    thermalResistance
  );

  const children: Paragraph[] = [
    // Заголовок
    createDoubleSeparatorLine(),
    createHeaderParagraph('РАСЧЁТ ВЫБОРА СЕЧЕНИЯ КАБЕЛЯ'),
    createHeaderParagraph('(Проверка по нагреву)', 24),
    createDoubleSeparatorLine(),
    createEmptyParagraph(),
    
    // Дата и режим
    createTextParagraph(`Дата расчёта: ${formatDateForReport(now)}`),
    createTextParagraph(`Режим расчёта: Нагрев (длительно допустимый ток)`),
    createEmptyParagraph(),
    
    // Раздел 1: Исходные данные
    createSeparatorLine(),
    createTextParagraph('1. Исходные данные', { bold: true, size: 26 }),
    createSeparatorLine(),
    createEmptyParagraph(),
    
    createParamLine('Ток нагрузки, I', formatNumber(input.current, 2), 'А'),
    createParamLine('Сечение проводника, S', formatNumber(input.crossSection, 1), 'мм²'),
    createParamLine('Материал проводника', getMaterialLabel(input.material)),
    createParamLine('Тип изоляции', getInsulationTypeLabel(input.insulationType)),
    createParamLine('Способ прокладки', getInstallationMethodLabel(input.installationMethod)),
    createParamLine('Температура среды, T_сред', formatNumber(input.ambientTemp, 0), '°C'),
    createParamLine('Количество кабелей рядом', formatNumber(input.cablesCount, 0)),
    createEmptyParagraph(),
    
    // Раздел 2: Формулы и ход расчёта
    createSeparatorLine(),
    createTextParagraph('2. Формулы и ход расчёта', { bold: true, size: 26 }),
    createSeparatorLine(),
    createEmptyParagraph(),
    
    // 2.1 Допустимый ток
    createTextParagraph('2.1. Длительно допустимый ток', { bold: true }),
    createTextParagraph('     По таблицам ПУЭ для заданных условий прокладки:'),
    createTextParagraph(`     Iдоп = ${formatNumber(result.allowedCurrent, 1)} А (табличное значение)`),
    createEmptyParagraph(),
    
    // 2.2 Поправочные коэффициенты
    createTextParagraph('2.2. Поправочные коэффициенты', { bold: true }),
    createTextParagraph(`     kпопр = kтемп × kпучок × kтруба × kлоток × kземля`),
    createEmptyParagraph(),
    
    // Детализация каждого коэффициента
    createTextParagraph('     Значения коэффициентов определяются автоматически по нормативным таблицам'),
    createTextParagraph('     на основании введённых пользователем условий прокладки:'),
    createEmptyParagraph(),
    
    // Температурный коэффициент
    ...getTempCoefficientDetails(input.insulationType, input.ambientTemp),
    
    // Коэффициент прокладки пучком
    ...getBundleCoefficientDetails(input.cablesCount, input.installationMethod),
    
    // Коэффициент прокладки в трубе
    ...getPipeCoefficientDetails(input.installationMethod),
    
    // Коэффициент прокладки в лотке
    ...getTrayCoefficientDetails(input.installationMethod),
    
    // Коэффициент прокладки в земле
    ...getGroundCoefficientDetails(input.installationMethod, thermalResistance),
    
    createEmptyParagraph(),
    createTextParagraph(`     Итого: kпопр = ${formatNumber(getTemperatureCoefficient(input.insulationType, input.ambientTemp), 3)} × ${formatNumber(getBundleCoeff(input.cablesCount), 3)} × ${formatNumber(input.installationMethod === 'pipe' ? 0.9 : 1, 3)} × ${formatNumber(input.installationMethod === 'tray' ? 0.95 : 1, 3)} × ${formatNumber(input.installationMethod === 'ground' ? getGroundCoeff(thermalResistance) : 1, 3)}`),
    createTextParagraph(`     Общий поправочный коэффициент: kпопр = ${formatNumber(correctionFactor, 3)}`),
    createEmptyParagraph(),
    
    // 2.3 Скорректированный ток
    createTextParagraph('2.3. Скорректированный допустимый ток', { bold: true }),
    createTextParagraph('     Iдоп.скорр = Iдоп × kпопр'),
    createTextParagraph(`     Iдоп.скорр = ${formatNumber(result.allowedCurrent, 1)} × ${formatNumber(correctionFactor, 3)} = ${formatNumber(result.correctedAllowedCurrent, 2)} А`),
    createEmptyParagraph(),
    
    // 2.4 Проверка условия
    createTextParagraph('2.4. Проверка условия', { bold: true }),
    createTextParagraph('     Условие: Iрасч ≤ Iдоп × kпопр'),
    createTextParagraph(`     ${formatNumber(result.calculatedCurrent, 2)} А ${result.isWithinNorm ? '≤' : '>'} ${formatNumber(result.correctedAllowedCurrent, 2)} А`),
    createEmptyParagraph(),
    
    // Раздел 3: Результаты
    createSeparatorLine(),
    createTextParagraph('3. Результаты расчёта', { bold: true, size: 26 }),
    createSeparatorLine(),
    createEmptyParagraph(),
    
    createParamLine('Расчётный ток, Iрасч', formatNumber(result.calculatedCurrent, 2), 'А'),
    createParamLine('Табличный допустимый ток, Iдоп', formatNumber(result.allowedCurrent, 1), 'А'),
    createParamLine('Поправочный коэффициент, kпопр', formatNumber(result.correctionFactor, 3)),
    createParamLine('Скорректированный допустимый ток', formatNumber(result.correctedAllowedCurrent, 2), 'А'),
    createParamLine('Запас по току', formatNumber(result.currentMargin, 1), '%'),
    createParamLine('Рекомендуемое сечение', formatNumber(result.recommendedSection, 1), 'мм²'),
    createEmptyParagraph(),
    
    // Раздел 4: Заключение
    createSeparatorLine(),
    createTextParagraph('4. Заключение', { bold: true, size: 26 }),
    createSeparatorLine(),
    createEmptyParagraph(),
    
    result.isWithinNorm
      ? createTextParagraph('⇒ Кабель проходит проверку по нагреву. Ток в норме.', { bold: true })
      : createTextParagraph('⇒ Превышение допустимого тока! Требуется увеличить сечение кабеля.', { bold: true }),
    createEmptyParagraph(),
    
    // Раздел 5: Комментарии
    createSeparatorLine(),
    createTextParagraph('5. Комментарии по нормативам', { bold: true, size: 26 }),
    createSeparatorLine(),
    createEmptyParagraph(),
    
    createTextParagraph('• Длительно допустимые токи – ПУЭ-7, таблица 1.3.4 (кабели с медными жилами, ПВХ изоляцией).'),
    createTextParagraph('• Поправочный коэффициент на температуру среды – ПУЭ-7, таблица 1.3.3.'),
    createTextParagraph('• Коэффициент на количество кабелей рядом – ГОСТ Р 50571.5.52-2011, таблица 4.'),
    createTextParagraph('• Коэффициент на прокладку в трубах/лотках – ПУЭ-7, п. 1.3.10.'),
    createTextParagraph(`• Тепловое сопротивление грунта: ${thermalResistance} К·см/Вт – ГОСТ Р 50571.5.52-2011, п. 523.3.`),
    createEmptyParagraph(),
    
    // Конец отчёта
    createSeparatorLine(),
    createTextParagraph('Конец отчёта.', { alignment: AlignmentType.CENTER }),
    createSeparatorLine(),
  ];

  return new Document({
    sections: [{
      properties: {},
      children,
    }],
  });
}

// ============================================================================
// СОХРАНЕНИЕ ДОКУМЕНТА
// ============================================================================

/**
 * Сохранение документа
 */
export async function saveDocument(
  document: Document, 
  filename: string
): Promise<void> {
  const blob = await Packer.toBlob(document);
  saveAs(blob, filename);
}

/**
 * Основная функция экспорта
 */
export async function exportToDocx(
  mode: 'voltage' | 'heating',
  input: CalculationInput,
  voltageLossResult?: VoltageLossResult | null,
  heatingResult?: HeatingResult | null
): Promise<void> {
  const now = new Date();
  const dateStr = formatDateForFilename(now);
  const filename = `Расчёт_сечения_кабеля_${dateStr}.docx`;

  if (mode === 'voltage' && voltageLossResult) {
    const doc = generateVoltageLossReport(input, voltageLossResult);
    await saveDocument(doc, filename);
  } else if (mode === 'heating' && heatingResult) {
    const doc = generateHeatingReport(input, heatingResult);
    await saveDocument(doc, filename);
  } else {
    throw new Error('Нет результатов расчёта для экспорта');
  }
}
